import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/lib/supabase';
import { parseOAuthCallbackDeepLink } from '@/utils/oauthCallbackDeepLink';
import { parseResetPasswordDeepLink } from '@/utils/resetPasswordDeepLink';
import { logger } from '@/utils/logger';

/**
 * Native-only: completes OAuth sign-in when the provider redirects back to the
 * app via the `com.malkkov.volyx://auth/callback` deep link, and routes the
 * password-reset deep link (`com.malkkov.volyx://reset-password`) into the
 * reset form after establishing the recovery session.
 *
 * The web flow is handled by `detectSessionInUrl` and needs nothing here.
 */
export function useOAuthDeepLink(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let removed = false;
    const handlePromise = App.addListener('appUrlOpen', async ({ url }) => {
      // Password reset: establish the recovery session here (native deep links
      // don't populate window.location, so the form can't read the token), then
      // hand off to ResetPasswordPage which finds the session via getSession().
      const reset = parseResetPasswordDeepLink(url);
      if (reset) {
        try {
          if (reset.tokenHash) {
            const { error } = await supabase.auth.verifyOtp({
              type: 'recovery',
              token_hash: reset.tokenHash,
            });
            if (error) throw error;
          } else if (reset.code) {
            const { error } = await supabase.auth.exchangeCodeForSession(reset.code);
            if (error) throw error;
          } else if (reset.errorDescription) {
            throw new Error(reset.errorDescription);
          }
        } catch (err) {
          logger.error('useOAuthDeepLink: failed to establish password recovery session', err);
        }
        try {
          await Browser.close();
        } catch {
          // No browser open / platform doesn't support close — ignore.
        }
        navigate('/reset-password', { replace: true });
        return;
      }

      const callback = parseOAuthCallbackDeepLink(url);
      if (!callback) return;

      if (callback.code) {
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(callback.code);
          if (error) throw error;
        } catch (err) {
          logger.error('useOAuthDeepLink: failed to exchange code for session', err);
        }
      }

      // Dismiss the in-app browser opened by startOAuth (best-effort).
      try {
        await Browser.close();
      } catch {
        // No browser open / platform doesn't support close — ignore.
      }

      // Hand off to AuthCallbackPage for profile creation + post-auth redirect.
      navigate(`/auth/callback${callback.forwardedSearch}`, { replace: true });
    });

    return () => {
      removed = true;
      void handlePromise.then((listener) => {
        if (removed) listener.remove();
      });
    };
  }, [navigate]);
}
